/**
 * Generates copy-paste request snippets for a gateway-authored operation, with
 * the input as the request body, in cURL, Node.js (fetch), and Python (requests). By default the API key is an
 * `ANYAPI_KEY` env var placeholder so a developer never pastes a real secret into
 * a snippet; pass `apiKey` to inline a real key (the dashboard quickstart does
 * this with the account's freshly minted key so the first call is runnable).
 */

import type { DiscoveryOperation } from '../data/discovery';

export type SnippetLang = 'curl' | 'node' | 'python';

/** An illustrative request for the dashboard quickstart - just for show; the real
 *  call (with full schemas) happens in the Playground. Defined once. */
export const EXAMPLE_SKU = 'reddit.subreddit_posts';
export const EXAMPLE_INPUT: Record<string, unknown> = { subreddit: 'webdev', limit: 10 };

export interface SnippetInput {
  baseUrl: string;
  operation: DiscoveryOperation;
  input: Record<string, unknown>;
  /** When set, inlined into the Authorization header instead of the env var. */
  apiKey?: string;
}

export interface Snippets {
  curl: string;
  node: string;
  python: string;
}

/** Indents a multi-line JSON string so it nests cleanly inside snippet bodies. */
const indent = (json: string, pad: string): string =>
  json
    .split('\n')
    .map((line, i) => (i === 0 ? line : pad + line))
    .join('\n');

export const buildSnippets = ({ baseUrl, operation, input, apiKey }: SnippetInput): Snippets => {
  const url = `${baseUrl}${operation.path}`;
  const body = input;
  const pretty = JSON.stringify(body, null, 2);

  const curlAuth = apiKey ? `Bearer ${apiKey}` : 'Bearer $ANYAPI_KEY';
  const curl = [
    `curl -X ${operation.method} ${url} \\`,
    `  -H "Authorization: ${curlAuth}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '${JSON.stringify(body)}'`,
  ].join('\n');

  const nodeAuth = apiKey ? `"Bearer ${apiKey}"` : '`Bearer ${process.env.ANYAPI_KEY}`';
  const node = [
    `const res = await fetch("${url}", {`,
    `  method: "${operation.method}",`,
    `  headers: {`,
    `    Authorization: ${nodeAuth},`,
    `    "Content-Type": "application/json",`,
    `  },`,
    `  body: JSON.stringify(${indent(pretty, '  ')}),`,
    `});`,
    `const data = await res.json();`,
    `console.log(data);`,
  ].join('\n');

  const pythonAuth = apiKey ? `"Bearer ${apiKey}"` : `f"Bearer {os.environ['ANYAPI_KEY']}"`;
  const python = [
    apiKey ? `import requests` : `import os, requests`,
    ``,
    `res = requests.${operation.method.toLowerCase()}(`,
    `    "${url}",`,
    `    headers={"Authorization": ${pythonAuth}},`,
    `    json=${indent(toPython(body), '    ')},`,
    `)`,
    `print(res.json())`,
  ].join('\n');

  return { curl, node, python };
};

/** Renders a JSON value as a Python literal (True/False/None, double-quoted keys). */
const toPython = (value: unknown): string => {
  const json = JSON.stringify(value, null, 4);
  return json
    .replace(/: true\b/g, ': True')
    .replace(/: false\b/g, ': False')
    .replace(/: null\b/g, ': None');
};
